/**
 * Geo hook + region-aware helpers.
 *
 * The backend `/api/geo` endpoint uses the ingress CDN's IP-country header
 * (Cloudflare `CF-IPCountry` / Vercel `X-Vercel-IP-Country`) to classify
 * the visitor as `IN` or `GLOBAL`. The result is cached in localStorage
 * (12h) so we don't hit the endpoint on every page load.
 *
 * Users can override the auto-detection via `setRegionOverride('IN'|'GLOBAL'|null)`.
 */
import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'amm_geo_v1';
const OVERRIDE_KEY = 'amm_geo_override';
const TTL_MS = 12 * 60 * 60 * 1000; // 12 h

const DEFAULT_GEO = { country: 'XX', region: 'GLOBAL', currency: 'USD', currency_symbol: '$' };
const IN_GEO = { country: 'IN', region: 'IN', currency: 'INR', currency_symbol: '₹' };

async function fetchGeo() {
  try {
    const res = await fetch(`${API}/api/geo`);
    if (!res.ok) return DEFAULT_GEO;
    return await res.json();
  } catch {
    return DEFAULT_GEO;
  }
}

export function useGeo() {
  const [geo, setGeo] = useState(() => {
    // Prefer manual override, then cache, then fallback
    const override = typeof window !== 'undefined' && localStorage.getItem(OVERRIDE_KEY);
    if (override === 'IN') return IN_GEO;
    if (override === 'GLOBAL') return DEFAULT_GEO;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const c = JSON.parse(raw);
        if (Date.now() - c.ts < TTL_MS) return c.geo;
      }
    } catch { /* noop */ }
    return DEFAULT_GEO;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(OVERRIDE_KEY)) return; // don't re-fetch when overridden
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw && Date.now() - JSON.parse(raw).ts < TTL_MS) return;
    } catch { /* noop */ }
    fetchGeo().then((g) => {
      setGeo(g);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ geo: g, ts: Date.now() })); } catch { /* noop */ }
    });
  }, []);

  const setOverride = useCallback((region) => {
    if (region === 'IN') {
      localStorage.setItem(OVERRIDE_KEY, 'IN');
      setGeo(IN_GEO);
    } else if (region === 'GLOBAL') {
      localStorage.setItem(OVERRIDE_KEY, 'GLOBAL');
      setGeo(DEFAULT_GEO);
    } else {
      localStorage.removeItem(OVERRIDE_KEY);
      localStorage.removeItem(CACHE_KEY);
      fetchGeo().then(setGeo);
    }
  }, []);

  const isOverridden = typeof window !== 'undefined' && !!localStorage.getItem(OVERRIDE_KEY);

  return { ...geo, setOverride, isOverridden };
}

// ---------- Price formatter ----------
export function formatPrice(amount, currency = 'INR') {
  if (amount == null || isNaN(amount)) return '-';
  const opts = { style: 'currency', currency, maximumFractionDigits: 0 };
  try {
    // INR → en-IN gives lakhs/crores formatting; USD → en-US
    const locale = currency === 'INR' ? 'en-IN' : 'en-US';
    return new Intl.NumberFormat(locale, opts).format(amount);
  } catch {
    const sym = currency === 'INR' ? '₹' : '$';
    return `${sym}${Number(amount).toLocaleString()}`;
  }
}

// ---------- Region-aware affiliate URL rewriter ----------
const IN_DOMAINS = {
  'amazon.com': 'amazon.in',
  'amzn.to': 'amzn.in',
  'flipkart.com': 'flipkart.com', // already .com — India-only site
  'ebay.com': 'ebay.in',
  'aliexpress.com': 'aliexpress.com', // stays same
};

const GLOBAL_DOMAINS = {
  'amazon.in': 'amazon.com',
  'amzn.in': 'amzn.to',
  'flipkart.com': 'amazon.com',        // no global equivalent — send to Amazon US
  'ebay.in': 'ebay.com',
};

export function regionAffiliateUrl(url, region = 'GLOBAL') {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const map = region === 'IN' ? IN_DOMAINS : GLOBAL_DOMAINS;
    if (map[host]) {
      u.hostname = map[host];
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}
